import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { ArrowUpRight, Clock, User } from 'lucide-react';

const Blog = () => {
  const posts = [
    {
      id: 1,
      title: 'The Future of Data Analytics: Trends to Watch in 2025',
      excerpt: 'Explore the emerging trends in data analytics that are reshaping how businesses make decisions and drive growth.',
      author: 'David Park',
      date: 'Dec 10, 2024',
      readTime: '8 min read',
      category: 'Data Analytics',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=800',
      featured: true,
    },
    {
      id: 2,
      title: 'Building a Successful Cloud Migration Strategy',
      excerpt: 'A comprehensive guide to planning and executing your organization\'s journey to the cloud.',
      author: 'Jennifer Liu',
      date: 'Dec 5, 2024',
      readTime: '6 min read',
      category: 'Cloud Solutions',
      image: 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?auto=format&fit=crop&q=80&w=800',
      featured: false,
    },
    {
      id: 3,
      title: 'How AI is Transforming Customer Experience',
      excerpt: 'Discover how artificial intelligence is revolutionizing the way businesses interact with their customers.',
      author: 'Marcus Johnson',
      date: 'Nov 28, 2024',
      readTime: '5 min read',
      category: 'AI & ML',
      image: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?auto=format&fit=crop&q=80&w=800',
      featured: false,
    },
    {
      id: 4,
      title: 'Data Governance Best Practices for Enterprise',
      excerpt: 'Learn the essential practices for managing and protecting your organization\'s data assets.',
      author: 'Sarah Mitchell',
      date: 'Nov 20, 2024',
      readTime: '7 min read',
      category: 'Data Analytics',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800',
      featured: false,
    },
    {
      id: 5,
      title: 'The ROI of Digital Transformation',
      excerpt: 'Understanding the true value of digital transformation investments and how to measure success.',
      author: 'David Park',
      date: 'Nov 15, 2024',
      readTime: '6 min read',
      category: 'Digital Transformation',
      image: 'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?auto=format&fit=crop&q=80&w=800',
      featured: false,
    },
    {
      id: 6,
      title: 'Machine Learning in Production: Lessons Learned',
      excerpt: 'Real-world insights from deploying machine learning models at scale in enterprise environments.',
      author: 'Jennifer Liu',
      date: 'Nov 8, 2024',
      readTime: '9 min read',
      category: 'AI & ML',
      image: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?auto=format&fit=crop&q=80&w=800',
      featured: false,
    },
  ];

  const featuredPost = posts.find(p => p.featured);
  const regularPosts = posts.filter(p => !p.featured);

  const categories = ['All', 'Data Analytics', 'Cloud Solutions', 'AI & ML', 'Digital Transformation'];

  return (
    <div className="min-h-screen" data-testid="blog-page">
      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 grain-texture" data-testid="blog-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="text-sm font-medium tracking-widest uppercase text-slate-500 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              Our Blog
            </p>
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight leading-[0.95] text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              Insights & Ideas
            </h1>
            <p className="text-lg md:text-xl text-slate-500 leading-relaxed">
              Stay updated with the latest trends, best practices, and thought leadership in data, cloud, and AI.
            </p>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-8 border-b border-slate-100" data-testid="blog-categories">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap gap-3">
            {categories.map((category, idx) => (
              <button
                key={idx}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  idx === 0 
                    ? 'bg-slate-900 text-white' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`category-${category.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Post */}
      {featuredPost && (
        <section className="py-16 md:py-20" data-testid="featured-post">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Card className="overflow-hidden border border-slate-100 rounded-2xl card-hover">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="aspect-video lg:aspect-auto">
                  <img 
                    src={featuredPost.image}
                    alt={featuredPost.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <div className="flex items-center gap-4 mb-4">
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                      Featured
                    </span>
                    <span className="text-sm text-slate-500">{featuredPost.category}</span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-4 group-hover:text-blue-600 transition-colors" style={{ fontFamily: 'var(--font-heading)' }}>
                    {featuredPost.title}
                  </h2>
                  <p className="text-slate-500 leading-relaxed mb-6">
                    {featuredPost.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {featuredPost.author}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {featuredPost.readTime}
                      </span>
                    </div>
                    <span className="text-sm text-slate-400">{featuredPost.date}</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </section>
      )}

      {/* Blog Grid */}
      <section className="py-16 md:py-20 bg-slate-50" data-testid="blog-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {regularPosts.map((post) => (
              <Card 
                key={post.id}
                className="overflow-hidden border border-slate-100 bg-white rounded-xl card-hover group"
                data-testid={`blog-post-${post.id}`}
              >
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-medium text-blue-600">{post.category}</span>
                    <span className="text-xs text-slate-400">{post.date}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors" style={{ fontFamily: 'var(--font-heading)' }}>
                    {post.title}
                  </h3>
                  <p className="text-slate-500 text-sm leading-relaxed mb-4 line-clamp-2">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <User className="w-3 h-3" />
                      <span>{post.author}</span>
                    </div>
                    <div className="flex items-center gap-1 text-sm text-slate-500">
                      <Clock className="w-3 h-3" />
                      <span>{post.readTime}</span>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-24 md:py-32" data-testid="newsletter-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Subscribe to Our Newsletter
            </h2>
            <p className="text-slate-500 mb-8">
              Get the latest insights delivered straight to your inbox. No spam, just valuable content.
            </p>
            <form className="flex flex-col sm:flex-row gap-4" data-testid="newsletter-form">
              <input 
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-6 py-3 border border-slate-200 rounded-full focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                data-testid="newsletter-email"
              />
              <button 
                type="submit"
                className="px-8 py-3 bg-slate-900 text-white rounded-full hover:bg-slate-800 transition-colors btn-click"
                data-testid="newsletter-submit"
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
